/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import type { Type } from '@warp-drive/core/types/symbols';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array } from 'ember-data-model-fragments/attributes';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';
import Day from 'linced-app/enums/medical/day';
import type Log from 'linced-app/models/medical/log';
import type Medication from 'linced-app/models/medical/medication';
import type School from 'linced-app/models/school';

// Proto: medical.Schedule.
export default class Schedule extends Model {
  declare [Type]: 'medical/schedule';

  Day = Day;

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the school the sickbay attendance record is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // medicationId is the id of the medication the schedule relates to
  @belongsTo('medical/medication', { async: true, inverse: 'schedules' })
  declare medicationId: AsyncBelongsTo<Medication>;

  @alias('medicationId')
  declare medication: AsyncBelongsTo<Medication>;

  @alias('medication.id')
  declare medication_id: string | null | undefined;

  // time is the time of day the schedule relates to in HH:mm format
  @attr('string')
  declare time: string | undefined;

  // days are the days the schedule relates to
  @array('number')
  declare days: StatefulArray<number>;

  // startDate is the start date for administration of the schedule in YYYY-MM-DD format
  @attr('string')
  declare startDate: string | undefined;

  // endDate is the end date for administration of the schedule in YYYY-MM-DD format
  @attr('string')
  declare endDate: string | undefined;

  // dosage is the dosage for administration of the schedule
  @attr('string', { defaultValue: '' })
  declare dosage: string;

  // notifyCaregiver indicates if the caregiver should be
  // notified when the scheduled medication is administered
  @attr('boolean', { defaultValue: false })
  declare notifyCaregiver: boolean;

  // deleted is used to implement soft-deletes
  @attr('boolean', { defaultValue: false })
  declare deleted: boolean;

  @hasMany('medical/log', { async: true, inverse: 'scheduleId' })
  declare logs: AsyncHasMany<Log>;

  @tracked todayDate = moment().format('YYYY-MM-DD');
  todayDayOfWeek = moment().isoWeekday();

  @computed('notifyCaregiver')
  get notifyCaregiverString() {
    return this.notifyCaregiver ? 'Yes' : 'No';
  }

  @computed('todayDate', 'startDate', 'endDate')
  get isCurrent() {
    // TS-ZERO(startDate)
    if (this.todayDate < this.startDate!) {
      return false;
    }
    // TS-ZERO(endDate)
    if (this.todayDate > this.endDate!) {
      return false;
    }
    return true;
  }

  @computed('isCurrent', 'days.[]')
  get scheduledToday() {
    return this.isCurrent && !!this.days.includes(this.todayDayOfWeek);
  }

  @computed('logs.content.[]', 'todayDate')
  get todaysLogs() {
    return resolved(this.logs).filter((log) => {
      return (
        // TS-ZERO(displayDate)
        moment.unix(log.displayDate!).format('YYYY-MM-DD') === this.todayDate
      );
    });
  }

  @computed('todaysLogs.[]')
  get administeredToday() {
    return !!this.todaysLogs.length;
  }

  @computed('todaysLogs.[]')
  get todaysLog() {
    return this.todaysLogs[0];
  }

  @computed('days.[]')
  get daysString() {
    return this.days
      .toArray()
      .sort()
      .map((day) => {
        return this.Day.getLabel(day);
      })
      .join(', ');
  }

  @computed(
    'medication_id',
    'time',
    'days.[]',
    'startDate',
    'endDate',
    'dosage',
  )
  get isValid() {
    if (isEmpty(this.medication_id)) {
      return false;
    }
    if (isEmpty(this.time)) {
      return false;
    }
    if (this.days.length < 1) {
      return false;
    }
    if (isEmpty(this.startDate)) {
      return false;
    }
    if (isEmpty(this.endDate)) {
      return false;
    }
    if (isEmpty(this.dosage)) {
      return false;
    }

    return true;
  }

  @computed('time')
  get isLate() {
    return moment().unix() > moment(this.time, 'HH:mm').unix();
  }
}
